/**
 * Who started a thread and who else wrote in it — for the thread row's icons
 * (big icon = starter, small icons = the others).
 *
 * Zimbra's conversation summary lists participants in no usable order (measured
 * 2026-10-16: "Gamle vindmølle" lists rfj, ap, Peter though Peter wrote first),
 * so this works from the thread's own messages.
 */

export interface ThreadMsg {
  /** sender address */
  a?: string;
  /** sender display name */
  p?: string;
  d?: string;
  /** Zimbra flags; 's' = sent by me */
  f?: string;
}

export interface Person {
  email: string;
  name: string;
}

export interface ThreadPeople {
  starter: Person;
  /** the others who wrote, newest first, at most `max` */
  others: Person[];
  /** how many more are not shown */
  more: number;
}

function person(m: ThreadMsg): Person | null {
  const email = (m.a ?? '').trim().toLowerCase();
  if (!email) return null;
  return { email, name: (m.p ?? m.d ?? '').trim() || email };
}

/**
 * `msgs` are the thread's messages NEWEST FIRST. The starter is the sender of the
 * oldest mail that came in (not sent by me); a thread of only my own mails is
 * started by me. Others = everyone else who wrote (me included when I replied),
 * newest first, deduplicated.
 */
export function threadPeople(msgs: ThreadMsg[], self: string, max = 4): ThreadPeople | null {
  const me = self.trim().toLowerCase();
  const mine = (m: ThreadMsg) => (m.f ?? '').includes('s') || (m.a ?? '').trim().toLowerCase() === me;
  const oldestFirst = [...msgs].reverse();
  const first = oldestFirst.find((m) => !mine(m) && person(m)) ?? oldestFirst.find((m) => person(m));
  const starter = first ? person(first) : null;
  if (!starter) return null;
  const seen = new Set([starter.email]);
  const all: Person[] = [];
  for (const m of msgs) {
    const p = person(m);
    if (!p || seen.has(p.email)) continue;
    seen.add(p.email);
    all.push(p);
  }
  return { starter, others: all.slice(0, max), more: Math.max(0, all.length - max) };
}
